import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Music4, Trophy, Play } from 'lucide-react';

const PADS = [0, 1, 2, 3];

export default function Sequence() {
  const [seq, setSeq] = useState([]);
  const [state, setState] = useState('idle'); // idle | showing | input | over
  const [active, setActive] = useState(null);
  const [level, setLevel] = useState(0);
  const inputRef = useRef(0);
  const [best, setBest] = useState(() => { try { return Number(localStorage.getItem('spc_sequence_best')) || 0; } catch { return 0; } });
  const timers = useRef([]);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  useEffect(() => () => clearTimers(), []);

  const playSequence = useCallback((s) => {
    setState('showing');
    setActive(null);
    clearTimers();
    s.forEach((pad, i) => {
      timers.current.push(setTimeout(() => setActive(pad), 600 * i + 200));
      timers.current.push(setTimeout(() => setActive(null), 600 * i + 550));
    });
    timers.current.push(setTimeout(() => { setState('input'); inputRef.current = 0; }, 600 * s.length + 300));
  }, []);

  const next = useCallback(() => {
    const s = [...seq, PADS[Math.floor(Math.random() * 4)]];
    setSeq(s);
    setLevel(s.length);
    playSequence(s);
  }, [seq, playSequence]);

  const start = () => { setSeq([]); setLevel(0); setState('showing'); setTimeout(() => { const s = [PADS[Math.floor(Math.random() * 4)]]; setSeq(s); setLevel(1); playSequence(s); }, 250); };

  const tap = (pad) => {
    if (state !== 'input') return;
    setActive(pad);
    setTimeout(() => setActive(null), 180);
    if (pad === seq[inputRef.current]) {
      inputRef.current += 1;
      if (inputRef.current === seq.length) {
        const reached = seq.length;
        if (reached > best) { setBest(reached); try { localStorage.setItem('spc_sequence_best', String(reached)); } catch {} }
        setState('showing');
        setTimeout(next, 700);
      }
    } else {
      clearTimers();
      setState('over');
    }
  };

  const statusLabel = {
    idle: 'Press play, watch the pattern, then repeat it.',
    showing: 'Watch closely…',
    input: 'Your turn — repeat the pattern!',
    over: `Out of rhythm! You reached level ${level}.`,
  }[state];

  return (
    <div className="solo-game" data-testid="solo-sequence">
      <div className="solo-head">
        <div className="solo-title"><Music4 size={18} /><span>Cadence</span></div>
        <div className="solo-score">
          {best > 0 && <span className="pill yellow"><Trophy size={12} /> Best lvl {best}</span>}
          {state !== 'idle' && <span className="pill">Level {level}</span>}
        </div>
      </div>

      <div className="seq-grid" data-testid="seq-grid">
        {PADS.map(p => (
          <button key={p} className={`seq-pad seq-pad-${p} ${active === p ? 'lit' : ''}`}
            disabled={state !== 'input'} onClick={() => tap(p)} data-testid={`seq-pad-${p}`} aria-label={`Pad ${p + 1}`} />
        ))}
      </div>

      <div className={`solo-status ${state === 'over' ? 'lose' : state === 'input' ? 'win' : 'draw'}`} data-testid="seq-status">{statusLabel}</div>

      {(state === 'idle' || state === 'over') && (
        <button className="btn" onClick={start} data-testid="seq-start"><Play size={15} /> {state === 'over' ? 'Play again' : 'Start'}</button>
      )}
    </div>
  );
}
